// src/pages/HomePage.tsx
import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { getNextUnlockOnion } from "../services/collectionService";
import { addCollection } from "../api/collectionApi";
import "../styles/HomePage.css";

import {
  getTodos,
  addTodo,
  toggleTodo,
  deleteTodo,
  editTodo,
} from "../api/todolistApi";

import ProgressBar from "../components/ProgressBar";
import CalendarView from "../components/CalendarView";
import RightPanel from "../components/RightPanel";
import OnionDisplay from "../components/OnionDisplay";
import { saveDiary, getDiaryDates } from "../api/diaryApi";
import { MoodType } from "../types/appTypes";

const HomePage: React.FC = () => {
  const navigate = useNavigate();

  /* ================= 상태 ================= */
  const [userId, setUserId] = useState<string | null>(null);
  const [todos, setTodos] = useState<any[]>([]);
  const [score, setScore] = useState(0);

  // 하루 4회 + 중복 체크 방지
  const completedTodoIdsRef = useRef<Set<number>>(new Set());
  const todayConsumedCountRef = useRef<number>(0);

  // 단계 경계 감지용
  const prevScoreRef = useRef<number>(0);

  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [diaryDates, setDiaryDates] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<"todo" | "diary">("todo");

  /* ================= 로그인 ================= */
  useEffect(() => {
    const uid = localStorage.getItem("user_id");
    if (!uid) navigate("/login");
    else setUserId(uid);
  }, [navigate]);

  /* ================= score 로드 ================= */
  const loadGauge = useCallback(async () => {
    if (!userId) return;

    const { data } = await supabase
      .from("gauge")
      .select("total_progress")
      .eq("user_id", userId)
      .single();

    const value = data?.total_progress ?? 0;
    setScore(value);
    prevScoreRef.current = value;
  }, [userId]);

  /* ================= todos 로드 ================= */
  const loadTodos = useCallback(async () => {
    if (!userId) return;
    const todosData = await getTodos(userId, selectedDate);
    setTodos(todosData);
  }, [userId, selectedDate]);

  useEffect(() => {
    if (!userId) return;
    loadGauge();
  }, [userId, loadGauge]);

  useEffect(() => {
    if (!userId) return;
    loadTodos();

    // 날짜 변경 시 하루 카운트 초기화
    completedTodoIdsRef.current.clear();
    todayConsumedCountRef.current = 0;
  }, [userId, selectedDate, loadTodos]);

  useEffect(() => {
    if (!userId) return;
    getDiaryDates(userId).then((dates) => {
      setDiaryDates(new Set(dates));
    });
  }, [userId]);

  /* ================= 단계 도달 감지 ================= */
  useEffect(() => {
    const prev = prevScoreRef.current;

    if (prev < 40 && score >= 40) {
      unlockOnce("onion_stage2");
    }

    if (prev < 80 && score >= 80) {
      unlockOnce("onion_stage3");
    }

    if (prev < 120 && score >= 120) {
      handleStage4Completed();
    }

    prevScoreRef.current = score;
  }, [score]);

  async function unlockOnce(onionType: string) {
    if (!userId) return;

    const { data } = await supabase
      .from("collection")
      .select("id")
      .eq("user_id", userId)
      .eq("onion_type", onionType)
      .maybeSingle();

    if (!data) {
      await addCollection(userId, onionType);
      alert(`🎉 ${onionType.replace("onion_", "")} 해금!`);
    }
  }

  async function handleStage4Completed() {
    if (!userId) return;

    const { data } = await supabase
      .from("collection")
      .select("onion_type")
      .eq("user_id", userId);

    const unlocked = data?.map((d) => d.onion_type) ?? [];
    const next = getNextUnlockOnion(unlocked);

    if (next) {
      await addCollection(userId, next);
      alert("🎉 양파가 성체가 되었어요!\n컬렉션에 추가되었습니다 🌱");
    }
  }

  /* ================= Todo ================= */
  async function handleToggle(id: number, current: boolean) {
    if (!userId) return;

    // 1️⃣ 상태 토글
    await toggleTodo(id, !current);

    // 2️⃣ false → true && 하루 4회 제한 && 중복 방지
    if (
      !current &&
      !completedTodoIdsRef.current.has(id) &&
      todayConsumedCountRef.current < 4
    ) {
      const { data, error } = await supabase.rpc("add_progress", {
        uid: userId,
        amount: 1,
      });

      if (!error && data?.length > 0) {
        setScore(data[0].total_progress);
        completedTodoIdsRef.current.add(id);
        todayConsumedCountRef.current += 1;
      }
    }

    loadTodos();
  }

  async function handleAdd(task: string) {
    if (!userId) return;
    await addTodo(userId, task, selectedDate);
    loadTodos();
  }

  async function handleEdit(id: number, newTask: string) {
    if (!userId) return;
    await editTodo(id, newTask);
    loadTodos();
  }

  async function handleDelete(id: number) {
    if (!userId) return;
    await deleteTodo(id);
    loadTodos();
  }

  async function handleDiarySave(
    id: number | null,
    date: string,
    mood: MoodType,
    text: string
  ) {
    if (!userId) return;

    await saveDiary(id, userId, date, mood, text);
    setDiaryDates((prev) => new Set(prev).add(date));

    // 저장 완료 알림 (구조 영향 없음)
    alert("🏷️일기가 저장되었습니다!");
  }

  if (!userId) return null;


  /* ================= Render ================= */
  return (
    <div className="homepage-container">
      <aside className="left-panel">
        <OnionDisplay score={score} />
      </aside>

      <div className="top-right-buttons">
        <button onClick={() => navigate("/collection")}>컬렉션</button>
        <button onClick={() => navigate("/user")}>사용자</button>
      </div>

      <main className="center-panel">
        <h4>양파 성장도</h4>
        <ProgressBar score={score} />

        <CalendarView
          selectedDate={selectedDate}
          onChangeDate={setSelectedDate}
          getDaySummary={(date) => {
            const dayTodos = todos.filter((t) => t.date === date);
            if (dayTodos.length === 0 && !diaryDates.has(date)) return null;
            return {
              donelength: dayTodos.filter((t) => t.is_done).length,
              totaldone: dayTodos.length,
              hasDiary: diaryDates.has(date),
            };
          }}
        />
      </main>

      <aside className="right-panel-wrapper">
        <RightPanel
          todos={todos}
          selectedDate={selectedDate}
          onAdd={handleAdd}
          onToggle={handleToggle}
          onDelete={handleDelete}
          onEdit={handleEdit}
          onDiarySave={handleDiarySave}
          activeTab={activeTab}
          onChangeTab={setActiveTab}
        />
      </aside>
    </div>
  );
};

export default HomePage;
